import { Button, Card, DatePicker, Descriptions, Form, Input, InputNumber, Message, Space, Table } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { WarehouseAPI } from "@/apis/material";
import { WoFulfillmentAPI } from "@/apis/manufacturing";
import { Number, Currency } from "@/components/Numeric";
import WarehouseSelect from "@/components/WarehouseSelect";
import _ from "lodash";

const LocationSelect = WarehouseSelect.LocationSelect;
const FormItem = Form.Item;

export const WoFulfillmentEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [form] = Form.useForm();
    const [info, setInfo] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [itemWarehouseIds, setItemWarehouseIds] = useState({});

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [data, warehouses] = await Promise.all([
                    WoFulfillmentAPI.get(params.id),
                    WarehouseAPI.actives(true)
                ]);
                setInfo(data);
                setWarehouseOptions(_.map(warehouses, w => ({ value: w.id, label: w.warehouseName })));
                const initialWarehouseIds = {};
                const finishedItems = (data.finishedItems || []).map((item, i) => {
                    if (item.warehouseId) {
                        initialWarehouseIds[i] = item.warehouseId;
                    }
                    return {
                        ...item,
                        _key: `fi-${i}`,
                        warehouseId: item.warehouseId || undefined,
                        warehouseName: item.warehouseName || "",
                        locationId: item.locationId || undefined,
                        locationName: item.locationName || "",
                        lot: item.lot || "",
                        expiredDate: item.expiredDate || undefined
                    };
                });
                const rawItems = (data.rawItems || []).map((item, i) => ({
                    ...item,
                    _key: `ri-${i}`,
                    usedQty: item.usedQty
                }));
                setItemWarehouseIds(initialWarehouseIds);
                form.setFieldsValue({ finishedItems, rawItems });
            } catch (error) {
                Message.error(error.message);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [params.id, form]);

    // Redistribute the total raw cost to finished items by their value
    // allocation ratio (null allocation falls back to an equal split, same as
    // the backend) so the Finished Items total always matches the Raw Items
    // total.
    const recalcFinishedItems = (finishedItems, totalRawCost) => {
        const list = finishedItems || [];
        if (list.length === 0) return list;
        return list.map(item => {
            const valueAmount = item.valueAllocation != null
                ? totalRawCost * (item.valueAllocation / 100)
                : totalRawCost / list.length;
            const quantity = item.quantity || 0;
            const cost = quantity > 0 ? valueAmount / quantity : 0;
            return { ...item, valueAmount, cost };
        });
    };

    const handleUsedQtyChange = (index, val) => {
        setInfo(prev => {
            const rawItems = [...(prev.rawItems || [])];
            const item = rawItems[index];
            if (!item) return prev;
            const usedQty = val || 0;
            rawItems[index] = { ...item, usedQty, valueAmount: usedQty * (item.cost || 0) };
            const totalRawCost = rawItems.reduce((sum, r) => sum + (r.valueAmount || 0), 0);
            return { ...prev, rawItems, finishedItems: recalcFinishedItems(prev.finishedItems, totalRawCost) };
        });
    };

    // The finished quantity is editable; all value amounts stay frozen and the
    // unit cost simply follows Value / Finished Qty
    const handleFinishedQtyChange = (index, val) => {
        setInfo(prev => {
            const finishedItems = [...(prev.finishedItems || [])];
            const item = finishedItems[index];
            if (!item) return prev;
            const quantity = val || 0;
            finishedItems[index] = {
                ...item,
                quantity,
                cost: quantity > 0 ? (item.valueAmount || 0) / quantity : 0
            };
            return { ...prev, finishedItems };
        });
    };

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const values = await form.validate();
            const finishedItems = (values.finishedItems || []).map(item => ({
                id: item.id,
                quantity: item.quantity,
                warehouseId: item.warehouseId,
                warehouseName: item.warehouseName,
                locationId: item.locationId,
                locationName: item.locationName,
                lot: item.lot,
                expiredDate: item.expiredDate
            }));
            const rawItems = (values.rawItems || []).map(item => ({
                id: item.id,
                usedQty: item.usedQty
            }));
            await WoFulfillmentAPI.update(params.id, { finishedItems, rawItems });
            Message.success("Fulfillment updated successfully");
            navigate(`/workspace/manufacturing/wo-fulfillment/${params.id}`);
        } catch (error) {
            if (error.status === "910002") {
                Message.error("Cannot modify fulfillment because its produced inventory has been picked, fulfilled, consumed, moved, or adjusted");
            } else {
                Message.error(error.message);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card title="Edit Fulfillment Order" extra={
            <Space>
                <Button type="primary" loading={saving} onClick={() => save()}>Save</Button>
                <Button onClick={() => navigate(`/workspace/manufacturing/wo-fulfillment/${params.id}`)}>Cancel</Button>
            </Space>
        }>
            <Descriptions colon=":" column={3} data={[
                { label: "Fulfillment Date", value: info.fulfillDate || "" },
                {
                    label: "Pick Order#",
                    value: (info.pickOrders || []).map(po =>
                        <Link key={po.woPickOrderId} to={`/workspace/manufacturing/wo-pick-order/${po.woPickOrderId}`}>{po.pickOrderNo}</Link>
                    ).reduce((acc, el, i) => i === 0 ? [el] : [...acc, ", ", el], [])
                },
                { label: "WO#", value: <Link to={`/workspace/manufacturing/work-order/${info.workOrderId}`}>{info.workOrderNo}</Link> },
                { label: "BOM Name", value: info.bomId ? <Link to={`/workspace/manufacturing/bom/${info.bomId}`}>{info.bomName}</Link> : info.bomName },
                { label: "BOM Description", value: info.bomDescription },
                { label: "Fulfilled Qty", value: <Number value={info.fulfilledQty} /> },
            ]} />

            <Form form={form}>
                <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Finished Items</div>
                <Table
                    rowKey="_key"
                    loading={loading}
                    columns={[
                        {
                            title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>),
                            dataIndex: "itemName",
                            render: (col, record) => (
                                <div style={{ lineHeight: 1.5 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1d2129' }}>{record.itemName || ""}</div>
                                    <div style={{ fontSize: 12, color: '#86909c' }}>{record.description || ""}</div>
                                </div>
                            )
                        },
                        { title: "BOM Qty", render: (col, record) => <Number value={record.bomQty} /> },
                        {
                            title: "Finished Qty", width: 140,
                            render: (col, record, index) => (
                                <FormItem field={`finishedItems[${index}].quantity`} noStyle>
                                    <InputNumber style={{ width: "100%" }} min={0.0001} onChange={(val) => handleFinishedQtyChange(index, val)} />
                                </FormItem>
                            )
                        },
                        { title: "UOM", dataIndex: "uom" },
                        { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                        { title: "Value", render: (col, record) => <Currency value={record.valueAmount} /> },
                        {
                            title: "Warehouse", width: 180,
                            render: (col, record, index) => (
                                <>
                                    <FormItem field={`finishedItems[${index}].id`} hidden><Input /></FormItem>
                                    <FormItem field={`finishedItems[${index}].warehouseName`} hidden><Input /></FormItem>
                                    <FormItem field={`finishedItems[${index}].warehouseId`} noStyle>
                                        <WarehouseSelect
                                            includeUnavailable
                                            style={{ width: "100%" }}
                                            onChange={(val) => {
                                                const warehouse = _.find(warehouseOptions, o => o.value === val);
                                                form.setFieldValue(`finishedItems[${index}].warehouseName`, warehouse ? warehouse.label : "");
                                                form.setFieldValue(`finishedItems[${index}].locationId`, undefined);
                                                form.setFieldValue(`finishedItems[${index}].locationName`, undefined);
                                                setItemWarehouseIds(prev => ({ ...prev, [index]: val }));
                                            }}
                                        />
                                    </FormItem>
                                </>
                            )
                        },
                        {
                            title: "Location", width: 150,
                            render: (col, record, index) => (
                                <>
                                    <FormItem field={`finishedItems[${index}].locationName`} hidden><Input /></FormItem>
                                    <FormItem field={`finishedItems[${index}].locationId`} noStyle>
                                        <LocationSelect
                                            warehouseId={itemWarehouseIds[index] !== undefined ? itemWarehouseIds[index] : form.getFieldValue(`finishedItems[${index}].warehouseId`)}
                                            onlyActive={false}
                                            style={{ width: "100%" }}
                                            onChange={(val, label) => {
                                                form.setFieldValue(`finishedItems[${index}].locationName`, label || "");
                                            }}
                                        />
                                    </FormItem>
                                </>
                            )
                        },
                        {
                            title: "Lot#", width: 160,
                            render: (col, record, index) => (
                                <FormItem field={`finishedItems[${index}].lot`} noStyle>
                                    <Input />
                                </FormItem>
                            )
                        },
                        {
                            title: "Expiration", width: 140,
                            render: (col, record, index) => (
                                <FormItem field={`finishedItems[${index}].expiredDate`} noStyle>
                                    <DatePicker style={{ width: "100%" }} />
                                </FormItem>
                            )
                        },
                    ]}
                    data={(info.finishedItems || []).map((item, i) => ({ ...item, _key: `fi-${i}` }))}
                    pagination={false}
                    summary={(currentData) => {
                        const totalValue = (currentData || []).reduce((sum, r) => sum + (r.valueAmount || 0), 0);
                        return (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell>Total</Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell><Currency value={totalValue} /></Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />

                <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Raw Items</div>
                <Table
                    rowKey="_key"
                    columns={[
                        {
                            title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>),
                            dataIndex: "itemName",
                            render: (col, record) => (
                                <div style={{ lineHeight: 1.5 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1d2129' }}>{record.itemName || ""}</div>
                                    <div style={{ fontSize: 12, color: '#86909c' }}>{record.description || ""}</div>
                                </div>
                            )
                        },
                        { title: "Pick Qty", render: (col, record) => <Number value={record.pickQty} /> },
                        {
                            title: "Used Qty", width: 120,
                            render: (col, record, index) => (
                                <>
                                    <FormItem field={`rawItems[${index}].id`} hidden><Input /></FormItem>
                                    <FormItem field={`rawItems[${index}].itemId`} hidden><Input /></FormItem>
                                    <FormItem field={`rawItems[${index}].usedQty`} noStyle>
                                        <InputNumber style={{ width: "100%" }} min={0} max={record.pickQty} onChange={(val) => handleUsedQtyChange(index, val)} />
                                    </FormItem>
                                </>
                            )
                        },
                        { title: "UOM", dataIndex: "uom" },
                        { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                        { title: "Value", render: (col, record) => <Currency value={record.valueAmount} /> },
                        {
                            title: "Warehouse Location",
                            render: (col, record) => {
                                const parts = [record.warehouseName, record.locationName].filter(Boolean);
                                return parts.join(" / ") || "-";
                            }
                        },
                        { title: "Lot#", dataIndex: "lot" },
                        { title: "Expiration", dataIndex: "expiredDate" },
                    ]}
                    data={(info.rawItems || []).map((item, i) => ({ ...item, _key: `ri-${i}` }))}
                    pagination={false}
                    summary={(currentData) => {
                        const totalValue = (currentData || []).reduce((sum, r) => sum + (r.valueAmount || 0), 0);
                        return (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell>Total</Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell><Currency value={totalValue} /></Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </Form>
        </Card>
    );
};
